// FILE: src/admin/pages/support/owners/tabs/SupportOwnerPayoutsTab.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  getSupportOwnerPayoutOverview,
  listSupportOwnerPayouts,
  listSupportOwnerPendingPayoutBookings,
  getSupportOwnerPayoutTxnDetail,
} from "../../../../../api/adminSupportApi";

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

/* --------------------- utils --------------------- */
function safeStr(x) {
  return x == null ? "" : String(x);
}
function up(x) {
  return safeStr(x).trim().toUpperCase();
}
function money(x) {
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}
function dt(x) {
  const s = safeStr(x).trim();
  if (!s) return "—";
  // backend often returns ISO; keep simple + consistent
  try {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return d.toLocaleString();
  } catch (_) {}
  return s;
}
function clip(s, n = 18) {
  const x = safeStr(s);
  if (!x) return "—";
  if (x.length <= n) return x;
  return x.slice(0, n) + "…";
}

/* --------------------- UI primitives --------------------- */
function Chip({ children, tone = "neutral" }) {
  const cls =
    tone === "good"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : tone === "warn"
      ? "border-amber-200 bg-amber-50 text-amber-900"
      : tone === "bad"
      ? "border-rose-200 bg-rose-50 text-rose-800"
      : "border-slate-200 bg-slate-50 text-slate-700";
  return (
    <span
      className={classNames(
        "inline-flex items-center rounded-md border px-3 py-1.5 text-[11px] font-semibold",
        cls
      )}
    >
      {children}
    </span>
  );
}

function Footer({ page, size, total, totalPages, setPage, setSize }) {
  const from = total ? page * size + 1 : 0;
  const to = total ? Math.min(total, (page + 1) * size) : 0;

  return (
    <div className="px-5 py-3 border-t border-slate-200 bg-white">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="text-xs text-slate-500">
          {total ? (
            <>
              Showing{" "}
              <span className="font-semibold text-slate-700">{from}</span>–<span className="font-semibold text-slate-700">{to}</span>{" "}
              of <span className="font-semibold text-slate-700">{total}</span>
            </>
          ) : (
            "No records"
          )}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="text-xs text-slate-500">View</div>
            <div className="relative">
              <select
                value={size}
                onChange={(e) => setSize(Number(e.target.value))}
                className="appearance-none h-10 rounded-md border border-slate-200 bg-white pl-3 pr-9 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                {[10, 20, 30, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>
              <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                ▼
              </span>
            </div>
          </div>

          <div className="text-sm text-slate-700">
            Page <span className="font-semibold">{page + 1}</span> of{" "}
            <span className="font-semibold">{Math.max(1, totalPages || 1)}</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
              disabled={page <= 0}
              onClick={() => setPage(page - 1)}
            >
              Prev
            </button>
            <button
              className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
              disabled={totalPages ? page >= totalPages - 1 : true}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Drawer({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70]">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full sm:w-[720px] bg-white shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-sm font-extrabold text-slate-900 truncate">{title}</div>
            <div className="mt-1 text-xs text-slate-500">Support-safe payout drilldown.</div>
          </div>
          <button
            onClick={onClose}
            className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50"
          >
            Close
          </button>
        </div>
        <div className="p-5 overflow-auto">{children}</div>
      </div>
    </div>
  );
}

/* =========================================================
   MAIN
   ========================================================= */

const TXN_STATUS = ["ALL", "QUEUED", "PROCESSING", "HOLD", "FAILED", "SUCCESS"];

export default function SupportOwnerPayoutsTab() {
  const { ownerId } = useOutletContext();

  // overview
  const [ov, setOv] = useState(null);
  const [ovErr, setOvErr] = useState("");
  const [ovBusy, setOvBusy] = useState(false);

  // pending bookings "view all"
  const [pendingMode, setPendingMode] = useState("SLICE"); // SLICE | ALL
  const [pbPage, setPbPage] = useState(0);
  const [pbSize, setPbSize] = useState(20);
  const [pbRows, setPbRows] = useState([]);
  const [pbTotal, setPbTotal] = useState(0);
  const [pbTotalPages, setPbTotalPages] = useState(0);
  const [pbBusy, setPbBusy] = useState(false);
  const [pbErr, setPbErr] = useState("");

  // txns list
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(30);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // drawer
  const [openTxnId, setOpenTxnId] = useState(null);
  const [txnDetail, setTxnDetail] = useState(null);
  const [txnBusy, setTxnBusy] = useState(false);
  const [txnErr, setTxnErr] = useState("");

  const reqRef = useRef(0);
  const ovRef = useRef(0);
  const pbRef = useRef(0);
  const txnRef = useRef(0);

  /* --------------------- load overview --------------------- */
  useEffect(() => {
    let alive = true;
    (async () => {
      const rid = ++ovRef.current;
      setOvBusy(true);
      setOvErr("");
      try {
        const res = await getSupportOwnerPayoutOverview({
          ownerId,
          pendingPage: 0,
          pendingSize: 10, // small slice for overview
        });
        if (!alive || rid !== ovRef.current) return;
        setOv(res || null);
      } catch (e) {
        if (!alive) return;
        setOvErr(e?.message || "Failed to load payout overview");
        setOv(null);
      } finally {
        if (alive) setOvBusy(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [ownerId]);

  /* --------------------- load pending bookings when ALL mode --------------------- */
  useEffect(() => {
    if (pendingMode !== "ALL") return;

    let alive = true;
    (async () => {
      const rid = ++pbRef.current;
      setPbBusy(true);
      setPbErr("");
      try {
        const res = await listSupportOwnerPendingPayoutBookings({
          ownerId,
          page: pbPage,
          size: pbSize,
        });
        if (!alive || rid !== pbRef.current) return;

        const content = Array.isArray(res?.content) ? res.content : [];
        setPbRows(content);
        setPbTotal(Number(res?.totalElements ?? content.length ?? 0));
        setPbTotalPages(Number(res?.totalPages ?? 0));
      } catch (e) {
        if (!alive) return;
        setPbErr(e?.message || "Failed to load pending payout bookings");
        setPbRows([]);
        setPbTotal(0);
        setPbTotalPages(0);
      } finally {
        if (alive) setPbBusy(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [pendingMode, ownerId, pbPage, pbSize]);

  /* --------------------- load txns list --------------------- */
  useEffect(() => {
    let alive = true;
    (async () => {
      const rid = ++reqRef.current;
      setBusy(true);
      setErr("");
      try {
        const res = await listSupportOwnerPayouts({
          ownerId,
          q: q.trim() ? q.trim() : undefined,
          status,
          page,
          size,
        });
        if (!alive || rid !== reqRef.current) return;

        const content = Array.isArray(res?.content) ? res.content : [];
        setRows(content);
        setTotal(Number(res?.totalElements ?? content.length ?? 0));
        setTotalPages(Number(res?.totalPages ?? 0));
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || "Failed to load payout transactions");
        setRows([]);
        setTotal(0);
        setTotalPages(0);
      } finally {
        if (alive) setBusy(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [ownerId, q, status, page, size]);

  /* --------------------- load txn detail for drawer --------------------- */
  useEffect(() => {
    if (!openTxnId) return;

    let alive = true;
    (async () => {
      const rid = ++txnRef.current;
      setTxnBusy(true);
      setTxnErr("");
      setTxnDetail(null);
      try {
        const res = await getSupportOwnerPayoutTxnDetail({
          ownerId,
          txnId: openTxnId,
        });
        if (!alive || rid !== txnRef.current) return;
        setTxnDetail(res || null);
      } catch (e) {
        if (!alive) return;
        setTxnErr(e?.message || "Failed to load payout txn detail");
      } finally {
        if (alive) setTxnBusy(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [ownerId, openTxnId]);

  /* --------------------- derived UI helpers --------------------- */
  const summary = ov?.summary || null;
  const account = ov?.account || null;
  const latestTxn = ov?.latestTxn || null;
  const pendingSlice = Array.isArray(ov?.pendingBookings) ? ov.pendingBookings : [];

  function txnTone(s) {
    const x = up(s);
    if (x === "SUCCESS") return "good";
    if (x === "FAILED") return "bad";
    if (x === "HOLD") return "warn";
    if (x === "QUEUED" || x === "PROCESSING") return "warn";
    return "neutral";
  }

  const txColgroup = useMemo(
    () => (
      <colgroup>
        <col style={{ width: "120px" }} />
        <col style={{ width: "140px" }} />
        <col style={{ width: "140px" }} />
        <col style={{ width: "170px" }} />
        <col style={{ width: "220px" }} />
        <col style={{ width: "260px" }} />
      </colgroup>
    ),
    []
  );

  const pbColgroup = useMemo(
    () => (
      <colgroup>
        <col style={{ width: "140px" }} />
        <col style={{ width: "140px" }} />
        <col style={{ width: "130px" }} />
        <col style={{ width: "130px" }} />
        <col style={{ width: "140px" }} />
        <col style={{ width: "240px" }} />
        <col style={{ width: "220px" }} />
      </colgroup>
    ),
    []
  );

  return (
    <div className="min-w-0 space-y-4">
      {/* =========================
          OVERVIEW
         ========================= */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="border-b border-slate-200 px-5 py-4">
          <div className="text-sm font-extrabold text-slate-900">Payouts</div>
          <div className="mt-1 text-xs text-slate-500">
            Support-safe payout account + pending payout eligibility + transactions.
          </div>
        </div>

        {ovErr ? (
          <div className="px-5 py-3 border-b border-slate-200 text-sm font-semibold text-rose-600">
            {ovErr}
          </div>
        ) : null}

        <div className="p-5">
          {ovBusy ? (
            <div className="text-sm text-slate-600">Loading overview…</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Account */}
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Payout Account
                    </div>
                    <div className="mt-1 text-sm font-extrabold text-slate-900">
                      {account ? up(account.method) || "—" : "Not set"}
                    </div>
                  </div>
                  {account ? (
                    <Chip tone={up(account.status) === "VERIFIED" ? "good" : up(account.status) === "ACTION_REQUIRED" ? "bad" : "warn"}>
                      {up(account.status) || "—"}
                    </Chip>
                  ) : (
                    <Chip tone="warn">MISSING</Chip>
                  )}
                </div>

                {account ? (
                  <div className="mt-3 space-y-2">
                    <div className="text-xs text-slate-600">
                      Ready:{" "}
                      <span className="font-semibold text-slate-900">
                        {account.readyForPayout ? "YES" : "NO"}
                      </span>
                    </div>

                    {account.issueNote ? (
                      <div className="text-xs text-rose-700 font-semibold">
                        {account.issueNote}
                      </div>
                    ) : null}

                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                          IFSC
                        </div>
                        <div className="mt-1 text-xs font-semibold text-slate-900">
                          {account.ifsc || "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                          Account
                        </div>
                        <div className="mt-1 text-xs font-semibold text-slate-900">
                          {account.accountNumberMasked || "—"}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                          UPI
                        </div>
                        <div className="mt-1 text-xs font-semibold text-slate-900">
                          {account.upiIdMasked || "—"}
                        </div>
                      </div>
                    </div>

                    <div className="pt-2 border-t border-slate-200">
                      <div className="text-[11px] text-slate-500">
                        Provider:{" "}
                        <span className="font-semibold text-slate-900">
                          {account.provider || "—"}
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-1">
                        Contact:{" "}
                        <span className="font-semibold text-slate-900 break-all">
                          {account.providerContactId || "—"}
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-1">
                        Fund:{" "}
                        <span className="font-semibold text-slate-900 break-all">
                          {account.providerFundAccountId || "—"}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 text-xs text-slate-600">
                    No payout account is linked for this owner.
                  </div>
                )}
              </div>

              {/* Summary */}
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Pending Summary
                </div>

                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                      Pending Bookings
                    </div>
                    <div className="mt-1 text-lg font-extrabold text-slate-900 tabular-nums">
                      {summary ? Number(summary.pendingBookingCount ?? 0) : 0}
                    </div>
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                      Pending Amount
                    </div>
                    <div className="mt-1 text-lg font-extrabold text-slate-900 tabular-nums">
                      ₹{money(summary?.pendingTotalAmount)}
                    </div>
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 col-span-2">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                          Pending Txns
                        </div>
                        <div className="mt-1 text-sm font-extrabold text-slate-900 tabular-nums">
                          {summary ? Number(summary.pendingTxnCount ?? 0) : 0}
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                          Latest Status
                        </div>
                        <div className="mt-1">
                          <Chip tone={txnTone(summary?.latestTxnStatus)}>
                            {up(summary?.latestTxnStatus) || "—"}
                          </Chip>
                        </div>
                        <div className="mt-1 text-[11px] text-slate-500">
                          {dt(summary?.latestTxnUpdatedAt)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Latest txn */}
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Latest Transaction
                    </div>
                    <div className="mt-1 text-sm font-extrabold text-slate-900">
                      {latestTxn ? `#${latestTxn.id}` : "—"}
                    </div>
                  </div>
                  {latestTxn ? (
                    <Chip tone={txnTone(latestTxn.status)}>{up(latestTxn.status) || "—"}</Chip>
                  ) : (
                    <Chip tone="neutral">NONE</Chip>
                  )}
                </div>

                {latestTxn ? (
                  <div className="mt-3 space-y-2">
                    <div className="text-xs text-slate-600">
                      Booking:{" "}
                      <span className="font-semibold text-slate-900 tabular-nums">
                        {latestTxn.bookingId ?? "—"}
                      </span>
                    </div>
                    <div className="text-xs text-slate-600">
                      Amount:{" "}
                      <span className="font-semibold text-slate-900 tabular-nums">
                        ₹{money(latestTxn.amount)} {latestTxn.currency || "INR"}
                      </span>
                    </div>
                    <div className="text-xs text-slate-600">
                      Provider:{" "}
                      <span className="font-semibold text-slate-900">
                        {latestTxn.provider || "—"}
                      </span>
                    </div>
                    <div className="text-xs text-slate-600 break-all">
                      Ref:{" "}
                      <span className="font-semibold text-slate-900">
                        {latestTxn.providerRef || "—"}
                      </span>
                    </div>

                    <div className="pt-2 border-t border-slate-200 text-[11px] text-slate-500">
                      Updated: <span className="font-semibold text-slate-900">{dt(latestTxn.updatedAt)}</span>
                    </div>

                    <button
                      onClick={() => setOpenTxnId(latestTxn.id)}
                      className="mt-2 h-10 w-full rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50"
                    >
                      Open txn details
                    </button>
                  </div>
                ) : (
                  <div className="mt-3 text-xs text-slate-600">
                    No payout transactions exist yet.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* =========================
          PENDING PAYOUT BOOKINGS
         ========================= */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="border-b border-slate-200 px-5 py-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-extrabold text-slate-900">Pending payout bookings</div>
            <div className="mt-1 text-xs text-slate-500">
              Eligible bookings (ride ended) that should be paid out to owner.
            </div>
          </div>

          <div className="flex items-center gap-2">
            {pendingMode === "SLICE" ? (
              <button
                onClick={() => {
                  setPendingMode("ALL");
                  setPbPage(0);
                }}
                className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50"
              >
                View all
              </button>
            ) : (
              <button
                onClick={() => {
                  setPendingMode("SLICE");
                  setPbPage(0);
                }}
                className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50"
              >
                Back to overview slice
              </button>
            )}
          </div>
        </div>

        {pendingMode === "SLICE" ? (
          <div className="p-5">
            {pendingSlice.length === 0 ? (
              <div className="text-sm text-slate-600">No eligible pending bookings.</div>
            ) : (
              <div className="space-y-2">
                {pendingSlice.map((b, i) => (
                  <div
                    key={b.bookingId ?? i}
                    className="rounded-xl border border-slate-200 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-extrabold text-slate-900 tabular-nums">
                        Booking #{b.bookingId ?? "—"}{" "}
                        <span className="text-xs font-semibold text-slate-500">
                          · {b.vehicleRegNo || "—"}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        {b.pickupLocation ? `${clip(b.pickupLocation, 36)} → ${clip(b.destination, 36)}` : "—"}
                      </div>
                      <div className="mt-1 text-[11px] text-slate-500">
                        Pickup: <span className="font-semibold text-slate-900">{b.pickupDate || "—"} {b.pickupTime || ""}</span>
                        {" · "}
                        Drop: <span className="font-semibold text-slate-900">{b.dropDate || "—"} {b.dropTime || ""}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      <div className="text-right">
                        <div className="text-[11px] text-slate-500">Owner net</div>
                        <div className="text-sm font-extrabold text-slate-900 tabular-nums">
                          ₹{money(b.ownerNet)}
                        </div>
                      </div>
                      <Chip tone={b.ownerPayoutStatus ? txnTone(b.ownerPayoutStatus) : "neutral"}>
                        {up(b.ownerPayoutStatus) || "NOT_PAID"}
                      </Chip>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            {pbErr ? (
              <div className="px-5 py-3 border-b border-slate-200 text-sm font-semibold text-rose-600">
                {pbErr}
              </div>
            ) : null}

            <div className="w-full overflow-x-auto">
              <table className="min-w-[1140px] w-full table-fixed">
                {pbColgroup}
                <thead className="bg-slate-50">
                  <tr className="border-b border-slate-200">
                    <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">
                      Booking
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">
                      Vehicle
                    </th>
                    <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-slate-600">
                      Owner Net
                    </th>
                    <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-slate-600">
                      Fare
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">
                      Route
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">
                      Payout
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-200">
                  {pbBusy ? (
                    <tr>
                      <td colSpan={7} className="px-5 py-8 text-sm text-slate-600">
                        Loading…
                      </td>
                    </tr>
                  ) : pbRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-5 py-10 text-center">
                        <div className="text-sm font-semibold text-slate-900">No pending bookings</div>
                        <div className="mt-1 text-xs text-slate-500">
                          No eligible bookings found for payout.
                        </div>
                      </td>
                    </tr>
                  ) : (
                    pbRows.map((b, idx) => (
                      <tr key={b.bookingId ?? idx} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50/30"}>
                        <td className="px-4 py-4">
                          <div className="text-sm font-semibold text-slate-900 tabular-nums">
                            #{b.bookingId ?? "—"}
                          </div>
                          <div className="mt-1 text-[11px] text-slate-500">
                            Updated: {dt(b.updatedAt)}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm font-semibold text-slate-900">
                            {b.vehicleRegNo || "—"}
                          </div>
                          <div className="mt-1 text-[11px] text-slate-500">
                            Pay: {up(b.paymentStatus) || "—"}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <div className="text-sm font-extrabold text-slate-900 tabular-nums">
                            ₹{money(b.ownerNet)}
                          </div>
                          <div className="mt-1 text-[11px] text-slate-500">
                            Gross: ₹{money(b.ownerGross)}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <div className="text-sm font-semibold text-slate-900 tabular-nums">
                            ₹{money(b.fare)}
                          </div>
                          <div className="mt-1 text-[11px] text-slate-500 tabular-nums">
                            Paid: ₹{money(b.paidAmount)}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <Chip tone="neutral">{up(b.status) || "—"}</Chip>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-xs font-semibold text-slate-900">
                            {b.pickupLocation ? clip(b.pickupLocation, 28) : "—"}
                          </div>
                          <div className="mt-1 text-[11px] text-slate-500">
                            → {b.destination ? clip(b.destination, 28) : "—"}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <Chip tone={b.ownerPayoutStatus ? txnTone(b.ownerPayoutStatus) : "neutral"}>
                            {up(b.ownerPayoutStatus) || "NOT_PAID"}
                          </Chip>
                          <div className="mt-1 text-[11px] text-slate-500 break-all">
                            {b.ownerPayoutRef || "—"}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <Footer
              page={pbPage}
              size={pbSize}
              total={pbTotal}
              totalPages={pbTotalPages}
              setPage={setPbPage}
              setSize={(n) => {
                setPbPage(0);
                setPbSize(n);
              }}
            />
          </>
        )}
      </div>

      {/* =========================
          TXNS LIST
         ========================= */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="border-b border-slate-200 px-5 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-extrabold text-slate-900">Payout transactions</div>
              <div className="mt-1 text-xs text-slate-500">
                Search and filter owner payout transactions.
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <div className="relative">
                <input
                  value={q}
                  onChange={(e) => {
                    setPage(0);
                    setQ(e.target.value);
                  }}
                  placeholder="Search txnId / bookingId / providerRef…"
                  className="h-10 w-full sm:w-[320px] rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 placeholder:text-slate-400"
                />
              </div>

              <div className="relative">
                <select
                  value={status}
                  onChange={(e) => {
                    setPage(0);
                    setStatus(e.target.value);
                  }}
                  className="appearance-none h-10 rounded-md border border-slate-200 bg-white pl-3 pr-9 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  {TXN_STATUS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                  ▼
                </span>
              </div>
            </div>
          </div>
        </div>

        {err ? (
          <div className="px-5 py-3 border-b border-slate-200 text-sm font-semibold text-rose-600">
            {err}
          </div>
        ) : null}

        <div className="w-full overflow-x-auto">
          <table className="min-w-[1050px] w-full table-fixed">
            {txColgroup}
            <thead className="bg-slate-50">
              <tr className="border-b border-slate-200">
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  Txn
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  Booking
                </th>
                <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  Amount
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  Provider
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  Failure
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200">
              {busy ? (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-sm text-slate-600">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-10 text-center">
                    <div className="text-sm font-semibold text-slate-900">No payouts</div>
                    <div className="mt-1 text-xs text-slate-500">
                      This owner has no payout transactions yet.
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => {
                  const s = up(r.status);
                  const tone = txnTone(s);
                  return (
                    <tr
                      key={r.id ?? idx}
                      className={classNames(
                        idx % 2 === 0 ? "bg-white" : "bg-slate-50/30",
                        "cursor-pointer hover:bg-amber-50/40"
                      )}
                      onClick={() => setOpenTxnId(r.id)}
                      title="Open txn detail"
                    >
                      <td className="px-4 py-4">
                        <div className="text-sm font-semibold text-slate-900 tabular-nums">
                          #{r.id ?? "—"}
                        </div>
                        <div className="mt-1 text-[11px] text-slate-500">
                          {dt(r.updatedAt || r.createdAt)}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm font-semibold text-slate-900 tabular-nums">
                          {r.bookingId ?? "—"}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="text-sm font-extrabold text-slate-900 tabular-nums">
                          ₹{money(r.amount)}
                        </div>
                        <div className="mt-1 text-[11px] text-slate-500">
                          {r.currency || "INR"}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <Chip tone={tone}>{s || "—"}</Chip>
                        <div className="mt-1 text-[11px] text-slate-500">
                          Attempts:{" "}
                          <span className="tabular-nums">{r.attemptCount ?? 0}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-xs font-semibold text-slate-900">
                          {r.provider || "—"}
                        </div>
                        <div className="mt-1 text-[11px] text-slate-500 break-all">
                          {r.providerRef || "—"}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        {r.failCode || r.failNote ? (
                          <>
                            <div className="text-xs font-semibold text-rose-700">
                              {r.failCode || "FAILED"}
                            </div>
                            <div className="mt-1 text-[11px] text-slate-500 line-clamp-2">
                              {r.failNote || "—"}
                            </div>
                          </>
                        ) : (
                          <div className="text-xs text-slate-500">—</div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <Footer
          page={page}
          size={size}
          total={total}
          totalPages={totalPages}
          setPage={setPage}
          setSize={(n) => {
            setPage(0);
            setSize(n);
          }}
        />
      </div>

      {/* =========================
          TXN DETAIL DRAWER
         ========================= */}
      <Drawer
        open={!!openTxnId}
        onClose={() => setOpenTxnId(null)}
        title={openTxnId ? `Payout txn #${openTxnId}` : "Payout txn"}
      >
        {txnErr ? (
          <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
            {txnErr}
          </div>
        ) : null}

        {txnBusy ? (
          <div className="text-sm text-slate-600">Loading txn detail…</div>
        ) : !txnDetail ? (
          <div className="text-sm text-slate-600">No detail.</div>
        ) : (
          <div className="space-y-4">
            {/* txn */}
            <div className="rounded-xl border border-slate-200 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Transaction
                  </div>
                  <div className="mt-1 text-sm font-extrabold text-slate-900 tabular-nums">
                    #{txnDetail?.txn?.id ?? "—"}
                  </div>
                </div>
                <Chip tone={txnTone(txnDetail?.txn?.status)}>
                  {up(txnDetail?.txn?.status) || "—"}
                </Chip>
              </div>

              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Amount
                  </div>
                  <div className="mt-1 text-sm font-extrabold text-slate-900 tabular-nums">
                    ₹{money(txnDetail?.txn?.amount)} {txnDetail?.txn?.currency || "INR"}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Booking
                  </div>
                  <div className="mt-1 text-sm font-extrabold text-slate-900 tabular-nums">
                    {txnDetail?.txn?.bookingId ?? "—"}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Provider
                  </div>
                  <div className="mt-1 text-xs font-semibold text-slate-900">
                    {txnDetail?.txn?.provider || "—"}
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500 break-all">
                    {txnDetail?.txn?.providerRef || "—"}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Attempts
                  </div>
                  <div className="mt-1 text-xs font-semibold text-slate-900 tabular-nums">
                    {txnDetail?.txn?.attemptCount ?? 0}
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500">
                    Last: {dt(txnDetail?.txn?.lastAttemptAt)}
                  </div>
                </div>
              </div>

              {txnDetail?.txn?.failCode || txnDetail?.txn?.failNote ? (
                <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-3">
                  <div className="text-xs font-extrabold text-rose-800">
                    {txnDetail?.txn?.failCode || "FAILED"}
                  </div>
                  <div className="mt-1 text-xs text-rose-800">
                    {txnDetail?.txn?.failNote || "—"}
                  </div>
                </div>
              ) : null}

              <div className="mt-3 text-[11px] text-slate-500">
                Created: <span className="font-semibold text-slate-900">{dt(txnDetail?.txn?.createdAt)}</span>
                {" · "}
                Updated: <span className="font-semibold text-slate-900">{dt(txnDetail?.txn?.updatedAt)}</span>
              </div>
            </div>

            {/* booking snapshot */}
            <div className="rounded-xl border border-slate-200 p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Booking snapshot
              </div>

              {txnDetail?.booking ? (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                      Booking
                    </div>
                    <div className="mt-1 text-sm font-extrabold text-slate-900 tabular-nums">
                      #{txnDetail.booking.bookingId ?? "—"}
                    </div>
                    <div className="mt-1 text-[11px] text-slate-500">
                      {up(txnDetail.booking.status) || "—"} · Pay {up(txnDetail.booking.paymentStatus) || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                      Vehicle
                    </div>
                    <div className="mt-1 text-xs font-semibold text-slate-900">
                      {txnDetail.booking.vehicleRegNo || "—"}
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                      Route
                    </div>
                    <div className="mt-1 text-xs text-slate-700">
                      {txnDetail.booking.pickupLocation
                        ? `${txnDetail.booking.pickupLocation} → ${txnDetail.booking.destination || "—"}`
                        : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                      Owner net
                    </div>
                    <div className="mt-1 text-sm font-extrabold text-slate-900 tabular-nums">
                      ₹{money(txnDetail.booking.ownerNet)}
                    </div>
                    <div className="mt-1 text-[11px] text-slate-500">
                      Gross: ₹{money(txnDetail.booking.ownerGross)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                      Fare / Paid
                    </div>
                    <div className="mt-1 text-xs font-semibold text-slate-900 tabular-nums">
                      ₹{money(txnDetail.booking.fare)} / ₹{money(txnDetail.booking.paidAmount)}
                    </div>
                    <div className="mt-1 text-[11px] text-slate-500">
                      Updated: {dt(txnDetail.booking.updatedAt)}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-2 text-sm text-slate-600">No booking snapshot.</div>
              )}
            </div>

            {/* payout account summary (quick) */}
            <div className="rounded-xl border border-slate-200 p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Payout account (summary)
              </div>

              {txnDetail?.payoutAccount ? (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                      Method / Status
                    </div>
                    <div className="mt-1">
                      <Chip tone={txnDetail.payoutAccount.status === "VERIFIED" ? "good" : "warn"}>
                        {up(txnDetail.payoutAccount.method) || "—"} · {up(txnDetail.payoutAccount.status) || "—"}
                      </Chip>
                    </div>
                    {txnDetail.payoutAccount.issueNote ? (
                      <div className="mt-2 text-xs font-semibold text-rose-700">
                        {txnDetail.payoutAccount.issueNote}
                      </div>
                    ) : null}
                  </div>
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                      Provider
                    </div>
                    <div className="mt-1 text-xs font-semibold text-slate-900">
                      {txnDetail.payoutAccount.provider || "—"}
                    </div>
                    <div className="mt-1 text-[11px] text-slate-500 break-all">
                      {txnDetail.payoutAccount.providerFundAccountId || "—"}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-2 text-sm text-slate-600">No payout account summary.</div>
              )}
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
